import React from 'react';
import useWizardReportDataContext from '../../../../contexts/useWizardReportData.context';
import { Accordion, AccordionDetails, AccordionSummary, Button, Checkbox, Divider, FormControlLabel, Grid, IconButton, TextField, Typography } from '@mui/material';
import PreviewGridComponent from './preview-grid/preview-grid.component';
import StyleColorPickerComponent from './style-color-picker/style-color-picker.component';
import { IReportStyleInformation } from '../../../../../../../shared/contracts/IReportStyleInformation';
import MuiIcon from '../../../../../../../shared/styles/mui/helpers/mui-icon.helper';
import { ExpandMore } from '@mui/icons-material';
import GridThemePickerComponent from './grid-theme-picker/grid-theme-picker.component';
import IGridTheme from './grid-theme-picker/IGridTheme';


/**
 * @author Pradeep R <pradeep.rajendran@rldatix.com>
 * @createdOn Jan/21/2025
 */
export default function WizardReportStylesComponent({

}: {}) {

    const { wizardData, applyReportData } = useWizardReportDataContext();
    const styleInfo = wizardData.reportSpecification.styleInformation;
    const [expanded, setExpanded] = React.useState<string | false>('themeSettingsPanel');
    const onPanelChange = (panel: string) => (e: React.SyntheticEvent, isExpanded: boolean) => {
        setExpanded(isExpanded ? panel : false);
    };

    const applyStyle = (prop: keyof IReportStyleInformation, value: string) => {
        applyReportData({
            ...wizardData,
            reportSpecification: {
                ...wizardData.reportSpecification,
                styleInformation: {
                    ...styleInfo,
                    [prop]: value
                }
            }
        })
    }

    //---
    return (<Grid container direction="row" sx={{ border: '0px solid red', pl: 3, pr: 0, pt: 4 }}>

        {/* Colors */}
        <Grid container item xs={4} direction='column' sx={{ px: 2 }}>

            <Accordion expanded={expanded === 'themeSettingsPanel'} onChange={onPanelChange('themeSettingsPanel')}
                sx={{ backgroundColor: 'white' }}
            >
                <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography component="span">Theme Settings</Typography>
                </AccordionSummary>

                <AccordionDetails>
                    {/* Colors-Header */}
                    <Grid container direction='row' alignItems='center' sx={{}}>
                        <Grid item xs>
                            <GridThemePickerComponent />
                        </Grid>
                        <Grid item xs={2} sx={{ textAlign: 'center', border: '0px solid red' }}>BG</Grid>
                        <Grid item xs={2} sx={{ textAlign: 'center', }}>FG</Grid>
                    </Grid>

                    <Divider sx={{ pt: 1 }} />

                    {/* Column Header */}
                    <Grid container direction='row' sx={{ pt: 1 }}>
                        <Grid item xs>Column Header color</Grid>
                        {/* Background */}
                        <Grid item xs={2} sx={{ textAlign: 'center', }}>
                            <StyleColorPickerComponent
                                color={wizardData?.reportSpecification.styleInformation.headerColor}
                                onChange={(color) => applyStyle('headerColor', color)}
                            />
                        </Grid>
                        {/* Foreground */}
                        <Grid item xs={2} sx={{ textAlign: 'center', border: '0px solid red' }}>
                            <StyleColorPickerComponent
                                color={wizardData?.reportSpecification.styleInformation.headerForegroundColor}
                                onChange={(color) => applyStyle('headerForegroundColor', color)}
                            />
                        </Grid>
                    </Grid>

                    {/* Default Row */}
                    <Grid container direction='row' sx={{ pt: 1 }}>
                        <Grid item xs>Default Row color</Grid>
                        {/* Background */}
                        <Grid item xs={2} sx={{ textAlign: 'center', }}>
                            <StyleColorPickerComponent
                                color={wizardData?.reportSpecification.styleInformation.itemColor}
                                onChange={(color) => applyStyle('itemColor', color)}
                            />
                        </Grid>
                        {/* Foreground */}
                        <Grid item xs={2} sx={{ textAlign: 'center', border: '0px solid red' }}>
                            <StyleColorPickerComponent
                                color={wizardData?.reportSpecification.styleInformation.itemForegroundColor}
                                onChange={(color) => applyStyle('itemForegroundColor', color)}
                            />
                        </Grid>
                    </Grid>

                    {/* Alternate Row */}
                    <Grid container direction='row' sx={{ pt: 1 }}>
                        <Grid item xs>Alternate Row color</Grid>
                        {/* Background */}
                        <Grid item xs={2} sx={{ textAlign: 'center', }}>
                            <StyleColorPickerComponent
                                color={wizardData?.reportSpecification.styleInformation.alternatingItemColor}
                                onChange={(color) => applyStyle('alternatingItemColor', color)}
                            />
                        </Grid>
                        {/* Foreground */}
                        <Grid item xs={2} sx={{ textAlign: 'center', border: '0px solid red' }}>
                            <StyleColorPickerComponent
                                color={wizardData?.reportSpecification.styleInformation.alternatingItemForegroundColor}
                                onChange={(color) => applyStyle('alternatingItemForegroundColor', color)}
                            />
                        </Grid>
                    </Grid>

                    <Divider sx={{ pt: 1 }} />

                    {/* Border Color */}
                    <Grid container direction='row' sx={{ pt: 1 }}>
                        <Grid item xs>Border color</Grid>
                        {/* Background */}
                        <Grid item xs={2} sx={{ textAlign: 'center', }}>
                            <StyleColorPickerComponent
                                color={wizardData?.reportSpecification.styleInformation.borderColor}
                                onChange={(color) => applyStyle('borderColor', color)}
                            />
                        </Grid>
                        <Grid item xs={2} sx={{ textAlign: 'center', border: '0px solid red' }}>

                        </Grid>
                    </Grid>

                    <Divider sx={{ pt: 1 }} />

                    {/* PageNation */}
                    <Grid container direction='row' sx={{ pt: 2 }}>
                        <Grid item xs>
                            <FormControlLabel label="Use Pagination"
                                control={<Checkbox color='primary' size='small' checked={styleInfo.usePaginationInWebView?.toLowerCase() === 'true'}
                                    onChange={(e) => applyStyle('usePaginationInWebView', e.target.checked ? 'true' : 'false')}
                                />} />
                        </Grid>
                        <Grid item xs={5} sx={{ textAlign: 'center', }}>
                            <TextField size="small" sx={{ ml: 1 }} disabled={styleInfo.usePaginationInWebView?.toLowerCase() !== 'true'}
                                type='number' label="Rows Per Page" value={+(styleInfo.itemsPerPage ?? 20)}
                                inputProps={{ inputMode: "numeric", pattern: "[0-9]*", }}
                                onChange={(e) => {
                                    const inputValue = e.target.value;
                                    if (/^\d*$/.test(inputValue)) {
                                        applyStyle('itemsPerPage', inputValue);
                                    }
                                }}
                            />
                        </Grid>
                    </Grid>
                </AccordionDetails>
            </Accordion>


            <Accordion expanded={expanded === 'pageSettingsPanel'} onChange={onPanelChange('pageSettingsPanel')}
                sx={{ backgroundColor: 'white' }}
            >
                <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography component="span">Page Settings</Typography>
                </AccordionSummary>

                <AccordionDetails>
                    <Grid container direction='row' sx={{}}>
                        <FormControlLabel label="Landscape Printing"
                            control={<Checkbox color='primary' size='small' checked={styleInfo.landscapePrinting?.toLowerCase() === 'true'}
                                onChange={(e) => applyStyle('landscapePrinting', e.target.checked ? 'true' : 'false')}
                            />} />
                    </Grid>

                    <Grid container direction='row' sx={{ pt: 0 }}>
                        <FormControlLabel label="Show date and time"
                            control={<Checkbox color='primary' size='small' checked={styleInfo.showDateTime?.toLowerCase() === 'true'}
                                onChange={(e) => applyStyle('showDateTime', e.target.checked ? 'true' : 'false')}
                            />} />
                    </Grid>

                    <Grid container direction='row' sx={{ pt: 0 }}>
                        <FormControlLabel label="Show page number"
                            control={<Checkbox color='primary' size='small' checked={styleInfo.showPageNumber?.toLowerCase() === 'true'}
                                onChange={(e) => applyStyle('showPageNumber', e.target.checked ? 'true' : 'false')}
                            />} />
                    </Grid>

                    <Grid container direction='row' sx={{ pt: 0 }}>
                        <FormControlLabel label="Add bookmark for each visual group"
                            control={<Checkbox color='primary' size='small' checked={styleInfo.addBookmarkForVisualGroup?.toLowerCase() === 'true'}
                                onChange={(e) => applyStyle('addBookmarkForVisualGroup', e.target.checked ? 'true' : 'false')}
                            />} />
                    </Grid>

                    <Grid container direction='row' sx={{ pt: 0 }}>
                        <FormControlLabel label="Page Break After Visual Groups (PDF)"
                            control={<Checkbox color='primary' size='small' checked={styleInfo.pageBreakAfterVisualGroups?.toLowerCase() === 'true'}
                                onChange={(e) => applyStyle('pageBreakAfterVisualGroups', e.target.checked ? 'true' : 'false')}
                            />} />
                    </Grid>

                    <Grid container direction='row' sx={{ pt: 0 }}>
                        <FormControlLabel label="Minimize Grid Width"
                            control={<Checkbox color='primary' size='small' checked={styleInfo.minimizeGridWidth?.toLowerCase() === 'true'}
                                onChange={(e) => applyStyle('minimizeGridWidth', e.target.checked ? 'true' : 'false')}
                            />} />
                    </Grid>
                </AccordionDetails>
            </Accordion>

        </Grid>

        {/* AgGrid Preview */}
        <Grid container item xs={8} direction='column' sx={{ px: 2, border: '0px solid blue' }}>
            <PreviewGridComponent />
        </Grid>

    </Grid >)
}